import React, { Fragment } from "react";
import { css } from "@emotion/react";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, TableHead, TableRow, TableCell, TableBody, TableHeaderCell } from "components/Table";
import { CheckBox } from "components/Form";
import { TableLoader } from "components/Loader";
import { MessagesError, MessagesNoResults } from "components/Messages";
import { DataTableCellContent, DataTableFooter } from "components/DataTable";
import { ExpandIcon } from "components/Icon";
import { getNestedFieldInObject } from "utils";
import { colors, margins } from "style";
import { AdvancedButton } from "components/Buttons";

/**
 * Data Table
 *
 * @param {Array}     data                       The data to populate the table
 * @param {Boolean}   loading                    The data loading state
 * @param {Object}    error                      The error data
 * @param {Array}     columns                    Array that returns column data for table
 * @param {Boolean}   selectable                 Defines whether row is selectable
 * @param {Function}  onSelect                   Function handler when a row is selected
 * @param {Array}     selectionInitialState      Define which rows are selected on mount
 * @param {Boolean}   expandable                 Defines whether row is expandable
 * @param {String}    expandableKeyName          Key name to expand row (field must be an array)
 * @param {Array}     expandableColumns          Array that returns column data for expanded row
 * @param {Function}  expandableRow              Function that returns a component used in place of expanded row
 * @param {Boolean}   clearable                  Defines whether table is clearable
 * @param {Number}    clearCacheValue            Changing value will clear table if clearable set to true
 * @param {Number}    resultsPerPage             Number of results per page
 * @param {Number}    currentPage                Current page number
 * @param {Function}  onClick                    Function handler when a row is clicked
 * @param {Array}     footer                     Array that returns column data for the footer
 * @param {Boolean}   hideHeader                 Hide the table head
 * @param {String}    marginSize                 Sets the horizontal margin size
 * @param {Function}  rowOptions                 Function that returns an array of options for each row
 * @param {Function}  headerOptions              Function that returns an array of options for the header
 * @param {Function}  filterFunction             Function that filters the data
 * @param {Function}  selectionKeyFunc           Function that helps identifying the unique identifier to be used for selection
 * @param {Array}     highlightedRowIds          Array of row ids to highlight
 * @param {Function}  rowOptionsTooltipIdFunc    Function that returns the tooltip for row options
 * @param {Function}  headerOptionsTooltipIdFunc Function that returns the tooltip for header options
 */
const DataTable = ({
  data: dataProp,
  loading,
  error: errorProp,
  columns,
  selectable,
  onSelect,
  selectionInitialState,
  emptyMessage,
  expandable,
  expandableKeyName,
  expandableColumns,
  expandableRow,
  clearable,
  clearCacheValue,
  resultsPerPage,
  currentPage,
  onClick,
  footer,
  hideHeader,
  marginSize,
  rowOptions,
  headerOptions,
  filterFunction,
  selectionKeyFunc,
  highlightedRowIds,
  selectFieldDisabledFunc,
  rowOptionsTooltipIdFunc,
  headerOptionsTooltipIdFunc,
  ...props
}) => {
  const [expanded, setExpanded] = useState([]);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(selectionInitialState);
  const [allDataIds, setAllDataIds] = useState([]);
  const [selectableDataIds, setSelectableDataIds] = useState([]);
  const [allItemsSelected, setAllItemsSelected] = useState(false);
  const [allItemsExpanded, setAllItemsExpanded] = useState(false);
  const [data, setData] = useState([]);
  const columnCount =
    columns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0) + (rowOptions || headerOptions ? 1 : 0);
  const isExpanded = (id, index) => expanded.indexOf(id || index) !== -1;
  const handleRowClick = (rowData) => typeof onClick === "function" && onClick(rowData);
  const isSelected = (id) => selected.indexOf(id) !== -1;
  const OptionsComponent = rowOptions?.component;
  const selectedSelectableIds = selected.filter((id) => selectableDataIds?.includes(id));

  useEffect(() => {
    if (selectionInitialState.length) {
      setSelected(selectionInitialState);
    }
  }, [selectionInitialState]);

  useEffect(() => {
    const currentIds = data?.map((obj) => selectionKeyFunc(obj)) || [];
    const currentSelectableIds =
      data?.filter((obj) => !selectFieldDisabledFunc(obj))?.map((obj) => selectionKeyFunc(obj)) || [];

    setAllDataIds(currentIds);
    setSelectableDataIds(currentSelectableIds);

    // Remove selected IDs that are not in the current data
    if (selected.length > 0 && currentIds?.length > 0) {
      setSelected((prev) => prev.filter((id) => currentIds.includes(id) && currentSelectableIds.includes(id)));
    }
  }, [data, selectionKeyFunc, selectFieldDisabledFunc]);

  useEffect(() => {
    if (!selectableDataIds || selectableDataIds.length === 0) {
      setAllItemsSelected(false);
      return;
    }

    setAllItemsSelected(selectableDataIds?.every((i) => selected?.includes(i)));
  }, [selectableDataIds, selected]);

  useEffect(() => {
    setAllItemsExpanded(allDataIds?.every((i) => expanded?.includes(i)));
  }, [allDataIds, expanded]);

  const handleSelection = (id) => {
    setSelected(isSelected(id) ? selected.filter((i) => i !== id) : [...selected, ...[id]]);
  };

  const handleSelectAll = () => {
    if (allItemsSelected) {
      return setSelected(selected.filter((i) => !selectableDataIds.includes(i)));
    }
    setSelected([...new Set([...selected, ...selectableDataIds])]);
  };

  const handleExpand = (id, index) => {
    const key = id !== null ? id : index;
    setExpanded(isExpanded(id, index) ? expanded.filter((i) => i !== key) : [...expanded, key]);
  };

  const handleExpandAll = () => {
    if (allItemsExpanded) {
      return setExpanded(expanded.filter((i) => !allDataIds.includes(i) && !data.some((_item, index) => index === i)));
    }
    setExpanded([...expanded, ...allDataIds, ...data.map((_, index) => index)]);
  };

  const handleOptionsCellClick = (e) => e.stopPropagation();

  const getGlobalDisplayIndex = (indexRow) => {
    if (typeof currentPage !== "number" || typeof resultsPerPage !== "number") {
      return indexRow + 1;
    }

    return (currentPage - 1) * resultsPerPage + indexRow + 1;
  };

  /**
   * @description When data changes, remove any selected items that no longer exist
   */
  useEffect(() => {
    if (clearable) {
      setSelected([]);
    }
  }, [data, clearable]);

  /**
   * @description Clear selection when changing tabs if clearable flag is true
   */
  useEffect(() => {
    if (clearable) {
      setSelected([]);
    }
  }, [clearCacheValue, clearable]);

  /**
   * @description Callback function when selected
   */
  useEffect(() => {
    if (typeof onSelect === "function") {
      onSelect(selected);
    }
  }, [onSelect, selected, data]);

  /**
   * @description Filter data based on filter function
   */
  useEffect(() => {
    setData(typeof filterFunction === "function" ? filterFunction(dataProp) : dataProp);
    setError(errorProp);
  }, [dataProp, filterFunction]);

  return (
    <>
      <Table selectable {...props}>
        {!hideHeader && (
          <TableHead>
            <TableRow>
              {selectable && (
                <TableHeaderCell scope="col" width={70} marginSize={marginSize}>
                  <CheckBox
                    name="check-all"
                    onChange={handleSelectAll}
                    value={allItemsSelected}
                    disabled={!selectableDataIds || selectableDataIds.length === 0}
                    noMargin
                  />
                </TableHeaderCell>
              )}
              {expandable && (
                <TableHeaderCell scope="col" width={selectable ? 20 : 60} expander>
                  <ExpandIcon isExpanded={allItemsExpanded} onClick={handleExpandAll} />
                </TableHeaderCell>
              )}
              {columns.map((columnData, index) => (
                <TableHeaderCell key={index} width={columnData.width || `auto`} scope="col" marginSize={marginSize}>
                  {columnData.label}
                </TableHeaderCell>
              ))}
              {(headerOptions || rowOptions) && (
                <TableHeaderCell width={80} scope="col" marginSize={marginSize}>
                  {headerOptions && (
                    <AdvancedButton
                      disabled={selectedSelectableIds.length === 0 || selectableDataIds.length === 0 || loading}
                      loading={loading}
                      options={headerOptions(selectedSelectableIds, data)}
                      tooltipIdFunc={() => headerOptionsTooltipIdFunc?.({ selected, data, selectableDataIds })}
                    />
                  )}
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHead>
        )}
        {(error || (!loading && (!data || data.length === 0))) && (
          <TableBody>
            <TableRow colSpan={100}>
              <TableCell colSpan={columnCount}>
                {error ? <MessagesError /> : <MessagesNoResults {...emptyMessage} />}
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        {(data || loading) && (
          <TableBody>
            <TableLoader loading={loading} numRows={resultsPerPage} numColumns={columnCount} />
            {!loading &&
              data?.map((rowData, indexRow) => (
                <Fragment key={rowData.id || indexRow}>
                  <TableRow
                    onClick={() => handleRowClick(rowData)}
                    className={typeof onClick === "function" || selectable ? `actionable` : ``}
                    expandable={expandable && isExpanded(rowData.id, indexRow)}
                    css={highlightedRowIds.includes(rowData.id) && styles.highlighted}
                  >
                    {selectable && (
                      <TableCell selected={isSelected(selectionKeyFunc(rowData))} marginSize={marginSize}>
                        <CheckBox
                          name={`check-${rowData.id}`}
                          onChange={() => {
                            handleSelection(selectionKeyFunc(rowData));
                          }}
                          value={isSelected(selectionKeyFunc(rowData))}
                          inTable
                          noMargin
                          disabled={selectFieldDisabledFunc(rowData)}
                        />
                      </TableCell>
                    )}
                    {expandable && (
                      <TableCell selected={isSelected(selectionKeyFunc(rowData))} marginSize={marginSize} expander>
                        <ExpandIcon
                          isExpanded={isExpanded(rowData.id, indexRow)}
                          onClick={() => handleExpand(rowData.id, indexRow)}
                          color={isSelected(selectionKeyFunc(rowData)) && `#fff`}
                        />
                      </TableCell>
                    )}
                    {columns.map((columnData, index) => (
                      <TableCell
                        key={index}
                        selected={isSelected(selectionKeyFunc(rowData))}
                        loading={loading}
                        marginSize={marginSize}
                        width={columnData.width || `auto`}
                        {...columnData.cellProps}
                      >
                        <DataTableCellContent
                          colData={columnData}
                          rowData={rowData}
                          previousRowData={data[indexRow - 1]}
                          displayIndex={getGlobalDisplayIndex(indexRow)}
                        />
                      </TableCell>
                    ))}
                    {(rowOptions || headerOptions) && (
                      <TableCell
                        selected={isSelected(selectionKeyFunc(rowData))}
                        marginSize={marginSize}
                        alignRight
                        onClick={handleOptionsCellClick}
                        width={80}
                      >
                        {rowOptions && typeof rowOptions === "function" ? (
                          <AdvancedButton
                            options={rowOptions(rowData)}
                            tooltipIdFunc={() => rowOptionsTooltipIdFunc?.(rowData)}
                          />
                        ) : rowOptions && typeof rowOptions !== "function" ? (
                          <OptionsComponent data={rowData} {...rowOptions.defaultArgs} />
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded(rowData.id, indexRow) &&
                    getNestedFieldInObject(rowData, expandableKeyName)?.map((expandedData, index) => (
                      <TableRow expanded key={expandedData.id || index}>
                        <TableCell colSpan={selectable ? 2 : 1} />
                        {expandableColumns?.map((columnData, index) => (
                          <TableCell key={index} {...columnData.cellProps}>
                            <DataTableCellContent colData={columnData} rowData={expandedData} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  {isExpanded(rowData.id, indexRow) && typeof expandableRow === "function" && (
                    <TableRow expanded>
                      <TableCell colSpan={columnCount}>{expandableRow(rowData, columnCount)}</TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
          </TableBody>
        )}
        {!error && !loading && data && <DataTableFooter data={footer} marginSize={marginSize} />}
      </Table>
    </>
  );
};

const styles = {
  highlighted: css`
    background-color: ${colors.lightYellow};
  `,
};

DataTable.defaultProps = {
  selectionInitialState: [],
  clearable: true,
  loading: false,
  hideHeader: false,
  selectable: false,
  marginSize: margins.normal,
  selectionKeyFunc: ({ id }) => id,
  highlightedRowIds: [],
  selectFieldDisabledFunc: () => false,
};

DataTable.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  footer: PropTypes.array,
  data: PropTypes.array,
  columns: PropTypes.array.isRequired,
  emptyMessage: PropTypes.object,
  selectable: PropTypes.bool,
  expandable: PropTypes.bool,
  expandableKeyName: PropTypes.string,
  expandableRow: PropTypes.func,
  expandableColumns: PropTypes.array,
  onSelect: PropTypes.func,
  resultsPerPage: PropTypes.number,
  onClick: PropTypes.func,
  selectionInitialState: PropTypes.array,
  clearable: PropTypes.bool,
  clearCacheValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentPage: PropTypes.number,
  hideHeader: PropTypes.bool,
  marginSize: PropTypes.string,
  rowOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.element)]),
  headerOptions: PropTypes.func,
  filterFunction: PropTypes.func,
  selectionKeyFunc: PropTypes.func,
  highlightedRowIds: PropTypes.array,
  selectFieldDisabledFunc: PropTypes.func,
  rowOptionsTooltipIdFunc: PropTypes.func,
  headerOptionsTooltipIdFunc: PropTypes.func,
};

export default DataTable;
